import React, {ForwardedRef, forwardRef, HTMLAttributes} from 'react';

import {buildIconHtml} from './markup';
import {SvgIconType} from './types';

// NOTE: This component depends on styles that aren't packaged with it. They come
// "for free" with mixpanel-common's default styles, which are included on
// essentially every page. See the git history of index.ts for the rationale.

export type SvgIconProps = {
  icon: SvgIconType;
  'has-unread'?: boolean;
  empty?: boolean;
  color?: string;
  height?: string;
  className?: string;
};

type SvgIconElementProps = SvgIconProps &
  Omit<HTMLAttributes<HTMLElement>, keyof SvgIconProps> & {
    hasUnread?: boolean;
    elref?: string;
  };

function SvgIconComponent(
  {
    icon,
    empty,
    color,
    height,
    className,
    style,
    children: _children,
    'has-unread': hasUnreadAttr,
    hasUnread,
    ...rest
  }: SvgIconElementProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const showUnread = hasUnreadAttr ?? hasUnread;
  const html = !icon || empty ? null : buildIconHtml(icon, color, height, showUnread);

  if (html === null) {
    if (icon && !empty) {
      // eslint-disable-next-line no-console
      console.error(`No svg-icon "${icon}" found.`);
    }
    return React.createElement(`svg-icon`, {
      ref,
      icon,
      class: className,
      style,
      ...rest,
    } as HTMLAttributes<HTMLElement>);
  }

  const dangerouslySetInnerHTML = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    __html: html,
  };

  return React.createElement(`svg-icon`, {
    ref,
    icon,
    // React 18 does not map `className` → the `class` attribute on custom elements,
    // so set `class` explicitly or tag/descendant CSS on the icon stops matching.
    class: className,
    style,
    dangerouslySetInnerHTML,
    ...rest,
  } as HTMLAttributes<HTMLElement>);
}

export const SvgIconReact = forwardRef(SvgIconComponent);
